'use client'

import { Avatar, AvatarFallback, AvatarImage } from '@mentora/ui-components'
import { Camera, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

interface Props {
  image: string | null
  name: string
}

export function AvatarUpload({ image, name }: Props) {
  const router = useRouter()
  const t = useTranslations('profile.avatar.errors')
  const tAvatar = useTranslations('profile.avatar')
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)

  async function handleFile(file: File) {
    setUploading(true)
    setPreview(URL.createObjectURL(file))

    const formData = new FormData()
    formData.append('file', file)

    try {
      const res = await fetch('/api/upload/avatar', {
        method: 'POST',
        body: formData,
      })

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string }
        const code = data.error
        const known = code === 'unauthorized' || code === 'noFile' || code === 'invalidType' || code === 'tooLarge'
        toast.error(known ? t(code) : t('uploadFailed'))
        setPreview(null)
        return
      }

      router.refresh()
    }
    catch {
      toast.error(t('uploadFailed'))
      setPreview(null)
    }
    finally {
      setUploading(false)
    }
  }

  return (
    <button
      type="button"
      className="group relative cursor-pointer rounded-full outline-none ring-2 ring-transparent transition-shadow focus-visible:ring-primary"
      onClick={() => inputRef.current?.click()}
      disabled={uploading}
    >
      <Avatar className="h-14 w-14">
        {preview ?? image
          ? <AvatarImage src={preview ?? image ?? undefined} alt={name} />
          : (
              <AvatarFallback className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {tAvatar('noImage')}
              </AvatarFallback>
            )}
      </Avatar>

      {/* Hover overlay */}
      <div className="absolute inset-0 flex items-center justify-center rounded-full bg-overlay/50 opacity-0 transition-opacity group-hover:opacity-100">
        {uploading
          ? (
              <Loader2 className="size-5 animate-spin text-overlay-foreground" />
            )
          : (
              <Camera className="size-5 text-overlay-foreground" />
            )}
      </div>

      {/* Loading overlay (always visible while uploading) */}
      {uploading && (
        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-overlay/50">
          <Loader2 className="size-5 animate-spin text-overlay-foreground" />
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file)
            handleFile(file)
          e.target.value = ''
        }}
      />
    </button>
  )
}
